import { rgba } from "polished";
import styled, { css } from "styled-components/macro";

import {
  borderRadius,
  breakpoint,
  colors,
  fontSize,
  fontStyle,
  iconSize,
  space,
} from "shared/styles/constants";
import { getStatus, hoverFocus } from "shared/styles/defaults";
import { ITheme } from "shared/types";

import { Link } from "react-router-dom";
import { IStatus } from "shared/types";

export interface StyledButtonProps {
  size?: "xs" | "s" | "m" | "l" | "huge";
  theme?: ITheme;
  /** Status of a button will override the theme */
  status?: IStatus.error;
  isFullWidth?: boolean;
  isFullWidthMobile?: boolean;
  fakeDisabled?: boolean;
  fakeHidden?: boolean; // fake hidden, still "clickable"
}

const getButtonColor = (
  theme: ITheme
): {
  color: colors;
  bgColor: colors;
  borderColor?: colors;
  hoverColor?: string | colors;
} => {
  switch (theme) {
    case ITheme.secondary:
      return {
        color: colors.white,
        bgColor: colors.grey1,
        borderColor: colors.grey2,
        hoverColor: colors.grey3,
      };
    case ITheme.tertiary:
      return {
        color: colors.black,
        bgColor: colors.white,
        hoverColor: rgba(colors.white, 0.85),
      };
    default:
      return {
        color: colors.white,
        bgColor: colors.green,
      };
  }
};

const ButtonStyles = () => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: ${({ isFullWidth }: StyledButtonProps) =>
    isFullWidth ? "100%" : "fit-content"};

  border: none;
  border-radius: ${borderRadius.s};

  ${fontStyle.bold}
  ${fontSize.s}

  ${hoverFocus.defaultFocus};

  svg {
    width: ${iconSize.m};
    height: ${iconSize.m};

    color: inherit;
  }

  > * + * {
    margin-left: ${space.xs};
  }

  ${breakpoint("s", "down")} {
    width: ${({ isFullWidthMobile, isFullWidth }: StyledButtonProps) =>
      isFullWidthMobile || (isFullWidthMobile === undefined && isFullWidth)
        ? "100%"
        : "fit-content"};
  }

  // theme
  border: 1px solid
    ${({ theme }) =>
      getButtonColor(theme).borderColor || getButtonColor(theme).bgColor};
  background-color: ${({ theme }) => getButtonColor(theme).bgColor};
  color: ${({ theme }) => getButtonColor(theme).color};
  ${({ theme }) =>
    hoverFocus.defaultHover(
      getButtonColor(theme).bgColor,
      getButtonColor(theme).hoverColor
    )};

  // status overrides the theme
  ${({ status }: StyledButtonProps) =>
    status &&
    css`
      border-color: ${getStatus(status).bgColor};
      background-color: ${getStatus(status).bgColor};
      color: ${getStatus(status).buttonTextColor};
      ${hoverFocus.defaultHover(getStatus(status).bgColor)};
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;

    ${({ theme }) =>
      theme === ITheme.tertiary &&
      css`
        border-color: ${rgba(colors.black, 0.5)};
        background-color: ${rgba(colors.black, 0.5)};
        color: ${colors.white};
      `}
  }

  ${({ fakeDisabled }: StyledButtonProps) =>
    fakeDisabled &&
    css`
      opacity: 0.6;
    `}

  // size
  ${({ size }: StyledButtonProps) => {
    switch (size) {
      case "xs":
        return css`
          padding: calc(${space.xxs} + 1px) ${space.s} calc(${space.xxs} + 2px);
          ${fontSize.xs}
        `;
      case "s":
        return css`
          padding: calc(${space.xxs} + 2px) ${space.m};
        `;
      case "huge":
        return css`
          padding: calc(${space.m} + 1px) ${space.m} calc(${space.m} + 2px);
          ${fontSize.xl}
        `;
      case "l":
        return css`
          padding: calc(${space.s} + 1px) ${space.l};
          ${fontSize.l}
        `;
      default:
      case "m":
        return css`
          padding: calc(${space.s} - 1px) ${space.m};
        `;
    }
  }}

  @media print {
    display: none;
  }
`;

export const StyledButton = styled.button<StyledButtonProps>`
  ${ButtonStyles}
`;

export const LinkButton = styled(Link)<StyledButtonProps>`
  ${ButtonStyles}

  text-decoration: none;
`;

export const InputButton = styled.input<StyledButtonProps>`
  ${ButtonStyles}
`;

export const LabelButton = styled.label<StyledButtonProps>`
  ${ButtonStyles}
`;

export interface StyledButtonContainerProps {
  spaceDirection?: "horizontal" | "vertical";
  align?: "flex-start" | "center" | "flex-end" | "stretch";
}

export const StyledButtonContainer = styled.div<StyledButtonContainerProps>`
  display: flex;
  flex-direction: ${({ spaceDirection }) =>
    spaceDirection === "horizontal" ? "row" : "column"};
  justify-content: ${({ align }) => align};
  flex-wrap: wrap;

  margin: -${space.xxs};

  > * {
    margin: ${space.xxs};
  }
`;

export interface StyledCloseButtonProps {
  filled?: boolean;
  theme?: ITheme;
}

export const StyledCloseButton = styled.button<StyledCloseButtonProps>`
  position: absolute;
  right: 0;
  top: 0;

  cursor: pointer;

  display: flex;
  padding: ${space.xs};
  border-radius: ${borderRadius.s};

  ${hoverFocus.defaultFocus}

  ${({ filled, theme }) =>
    filled &&
    css`
      border-radius: 50%;

      background-color: ${getButtonColor(theme).bgColor};
      color: ${getButtonColor(theme).color};

      ${hoverFocus.defaultHover(
        getButtonColor(theme).bgColor,
        getButtonColor(theme).hoverColor
      )};
    `}
`;
